// Cookie helpers for JWT tokens
// Note: No secrets stored in frontend code

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface JWTPayload {
  sub: string;
  role: string;
  orgId: string;
  managerId?: string;
  scopes: string[];
  jti: string;
  iat: number;
  exp: number;
}

// Cookie management utilities
export const authCookies = {
  // Set access token cookie (short-lived)
  setAccessToken: (token: string) => {
    document.cookie = `access_token=${token}; path=/; max-age=900; SameSite=Strict; Secure`;
  },

  // Set refresh token cookie (longer-lived)
  setRefreshToken: (token: string) => {
    document.cookie = `refresh_token=${token}; path=/; max-age=604800; SameSite=Strict; Secure; HttpOnly`;
  },

  // Get access token from cookies
  getAccessToken: (): string | null => {
    const cookies = document.cookie.split(';');
    const accessCookie = cookies.find(cookie => cookie.trim().startsWith('access_token='));
    return accessCookie ? accessCookie.split('=')[1] : null;
  },

  // Get refresh token from cookies
  getRefreshToken: (): string | null => {
    const cookies = document.cookie.split(';');
    const refreshCookie = cookies.find(cookie => cookie.trim().startsWith('refresh_token='));
    return refreshCookie ? refreshCookie.split('=')[1] : null;
  },

  // Clear all auth cookies
  clear: () => {
    document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  },
};

// JWT utility functions
export const jwtUtils = {
  // Decode JWT payload (client-side only, no verification)
  decode: (token: string): JWTPayload | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  },

  // Check if token is expired
  isExpired: (token: string): boolean => {
    const payload = jwtUtils.decode(token);
    if (!payload) return true;
    
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  },

  // Check if token will expire within specified minutes
  willExpireWithin: (token: string, minutes: number = 5): boolean => {
    const payload = jwtUtils.decode(token);
    if (!payload) return true;
    
    const now = Math.floor(Date.now() / 1000);
    const expirationBuffer = minutes * 60; // Convert minutes to seconds
    return payload.exp < (now + expirationBuffer);
  },

  // Get time until token expires (in seconds)
  getTimeUntilExpiration: (token: string): number => {
    const payload = jwtUtils.decode(token);
    if (!payload) return 0;
    
    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, payload.exp - now);
  },

  // Get token expiration date
  getExpirationDate: (token: string): Date | null => {
    const payload = jwtUtils.decode(token);
    if (!payload) return null;
    
    return new Date(payload.exp * 1000);
  },

  // Get user role from token
  getRole: (token: string): string | null => {
    const payload = jwtUtils.decode(token);
    return payload?.role || null;
  },

  // Get user organization ID from token
  getOrgId: (token: string): string | null => {
    const payload = jwtUtils.decode(token);
    return payload?.orgId || null;
  },

  // Get user manager ID from token
  getManagerId: (token: string): string | null => {
    const payload = jwtUtils.decode(token);
    return payload?.managerId || null;
  },
};

// Auth state management
export class AuthManager {
  private static instance: AuthManager;
  private currentUser: JWTPayload | null = null;

  private constructor() {}

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  // Initialize auth state from cookies
  initialize(): void {
    const accessToken = authCookies.getAccessToken();
    if (accessToken && !jwtUtils.isExpired(accessToken)) {
      this.currentUser = jwtUtils.decode(accessToken);
    }
  }

  // Get current user
  getCurrentUser(): JWTPayload | null {
    return this.currentUser;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    return this.currentUser?.role === role;
  }

  // Check if user has specific scope
  hasScope(scope: string): boolean {
    return this.currentUser?.scopes.includes(scope) || false;
  }

  // Logout user
  logout(): void {
    this.currentUser = null;
    authCookies.clear();
  }
}

export default AuthManager;
